import { incomeTaxYear } from "@stb/jurisdiction-au/income-tax"

import { displayYear } from "../../components/home/home-content"
import { formatAud, SimpleTable, SourceLine } from "../../components/landing"
import { PayCalculator } from "../../components/landing/pay-calculator"
import {
  answerSentence,
  bandRange,
  DEFAULT_FORM,
  DEFAULT_FY,
  evaluate,
  SHOWN_PERIODS,
  studyLoanRange,
} from "../../components/landing/pay-calculator-model"
import type { Guide } from "../guide-types"
import { payGuide } from "../pay-calculator"
import { englishDateToZh } from "./dates"

/**
 * Chinese counterpart of content/pay-calculator.tsx (T1321). Same engine, rates, sources and dates; only the words
 * differ. Terms: docs/design/landing/copy-zh.md.
 */
const year = incomeTaxYear(DEFAULT_FY)
const fy = displayYear(DEFAULT_FY)
const checked = englishDateToZh(payGuide.checked)
const startYear = Number(DEFAULT_FY.slice(0, 4))
const previousFy = displayYear(`${startYear - 1}-${String(startYear).slice(2)}`)

const dollars = (n: number) => `A$${n.toLocaleString("en-AU")}`
const percent = (bp: number) => `${bp / 100}%`
const whole = (v: Parameters<typeof formatAud>[0]) => formatAud(v).replace(/\.00$/, "")
const standardDeduction = dollars(year.standardDeduction?.maxDollars ?? 0)

const example = evaluate(DEFAULT_FORM)
const exampleRows = example.kind === "ok" ? example.result.perPeriod : undefined
const exampleDeduction =
  example.kind === "ok" && example.result.annual.standardDeduction.amount > 0
    ? example.result.annual.standardDeduction
    : undefined

/** Same hourly example as the English page: the engine works out the year and the tax. */
const HOURLY_RATE = 40
const hourly = evaluate({ ...DEFAULT_FORM, amount: String(HOURLY_RATE), period: "hour" })
const hourlyResult = hourly.kind === "ok" ? hourly.result : undefined

const brackets = year.resident.brackets
const bracketRows = brackets.map((b, i) => {
  const over =
    b.baseDollars === 0
      ? `超出 ${dollars(b.from)} 的部分，每 A$1 缴 ${b.rateBp / 100} 分`
      : `${dollars(b.baseDollars)}，另加超出 ${dollars(b.from)} 部分每 A$1 的 ${b.rateBp / 100} 分`
  return { header: bandRange(brackets, i, "zh"), cells: [b.rateBp === 0 ? "免缴" : over] }
})

const helpBands = year.studyLoan.bands
const helpRows = helpBands.map((b, i) => {
  const repayment =
    b.rateBp === 0
      ? "免缴"
      : b.ofWholeIncome
        ? `全部还款收入的 ${percent(b.rateBp)}`
        : b.baseDollars === 0
          ? `超出 ${dollars(b.from)} 部分的 ${percent(b.rateBp)}`
          : `${dollars(b.baseDollars)}，另加超出 ${dollars(b.from)} 部分的 ${percent(b.rateBp)}`
  return { header: studyLoanRange(helpBands, i, "zh"), cells: [repayment] }
})

const TAX_TABLES_URL = "https://www.ato.gov.au/tax-rates-and-codes/tax-tables-overview"

const PERIOD_ROWS = [
  ["税前收入", "gross"],
  ["所得税", "incomeTaxAfterLito"],
  ["Medicare levy", "medicareLevy"],
  ["到手收入", "takeHome"],
  ["雇主 super", "employerSuper"],
] as const

const periodLabel = { week: "每周", fortnight: "每两周", month: "每月", year: "每年" } as const

const linkClass =
  "focus-visible:ring-ring rounded-sm underline underline-offset-2 outline-none focus-visible:ring-2"

export const payGuideZh: Guide = {
  slug: payGuide.slug,
  title: "工资计算器：税后到手收入",
  description: `免费工资计算器，按 ATO ${fy} 财年税率算出税后到手收入，含所得税、Medicare levy 和 HELP 还款。可按周、两周、月或时薪计算，附 ATO 来源。`,
  crumb: "工资计算器",
  h1: `工资计算器：澳大利亚税后到手收入（${fy} 财年）`,
  published: payGuide.published,
  reviewed: payGuide.reviewed,
  badge: "免费工具",
  financialYear: fy,
  checked,
  sources: [
    { label: "ATO，澳大利亚居民的税率", href: payGuide.sources[0].href },
    { label: "ATO，Medicare levy 减免", href: year.medicare.source.url },
  ],
  answer: answerSentence(DEFAULT_FORM, formatAud, "zh"),
  answerMore: (
    <>
      计算器按 ATO {fy} 财年的税率，算出在澳大利亚打工的人扣除所得税、Medicare levy（医疗保险税）和
      HELP（高等教育贷款计划）还款后的到手收入。各州的税率相同。请在下面填入你自己的收入。
    </>
  ),
  related: [
    { label: "个体经营者税务计算器：ABN 收入该留出多少", href: "/sole-trader-tax-calculator" },
    { label: "GST 计算器：给价格加上 GST，或从中拆出 GST", href: "/gst-calculator" },
    { label: "Cents per km 计算器：算出你的汽车开支扣除额", href: "/cents-per-km-calculator" },
    { label: "ATO 行车记录簿要求：该记录什么", href: "/guides/ato-logbook-requirements" },
    { label: "免费发票生成器：开一张税务发票", href: "/invoice-generator" },
    { label: "个体经营者开发票：发出发票，看谁已付款", href: "/invoicing" },
  ],
  toc: [
    { id: "calculator", label: "算出你的到手收入" },
    { id: "periods", label: "每周、每两周、每月" },
    { id: "hourly", label: "从时薪算起" },
    { id: "payg", label: "PAYG 预扣税" },
    { id: "rates", label: `${fy} 财年税率` },
    { id: "help", label: "HELP 还款" },
    { id: "faq", label: "常见问题" },
  ],
  cta: {
    title: "同时有 ABN 收入？把账放在一处",
    description:
      "ABN 收入通常没有预扣税，要自己留出这笔钱。Kestly 把发票、收据和行程放在一处，报税时已经备好。",
    ctaLabel: "免费开始",
    ctaHref: "/login?from=pay-calculator",
  },
  body: (
    <>
      <section aria-labelledby="calculator" className="grid gap-3 md:gap-4">
        <h2 id="calculator" className="text-2xl font-semibold">
          算出你的到手收入
        </h2>
        <PayCalculator />
      </section>

      {exampleRows && (
        <section aria-labelledby="periods" className="grid gap-3">
          <h2 id="periods" className="text-2xl font-semibold">
            每周、每两周和每月的到手收入
          </h2>
          <p className="max-w-[70ch]">
            以下是居民在 {fy} 财年年薪 {whole(exampleRows.year.gross)} 的情况，不含 super
            {exampleDeduction && `，并已适用 ${whole(exampleDeduction)} 标准扣除额`}
            。每一列是全年金额除以 52、26 或 12。
          </p>
          <div className="hidden md:block">
            <SimpleTable
              caption={`${fy} 财年年薪 ${whole(exampleRows.year.gross)} 按发薪周期的到手收入`}
              columns={["", ...SHOWN_PERIODS.map((p) => periodLabel[p])]}
              rows={PERIOD_ROWS.map(([header, key]) => ({
                header,
                cells: SHOWN_PERIODS.map((p) => formatAud(exampleRows[p][key])),
              }))}
            />
          </div>
          <div className="grid gap-3 md:hidden">
            {SHOWN_PERIODS.map((p) => (
              <dl
                key={p}
                className="bg-card border-border grid gap-1 rounded-lg border p-3 text-sm"
              >
                <dt className="font-semibold">{periodLabel[p]}</dt>
                {PERIOD_ROWS.map(([label, key]) => (
                  <dd key={key} className="flex justify-between">
                    <span>{label}</span>
                    <span className="tabular-nums">{formatAud(exampleRows[p][key])}</span>
                  </dd>
                ))}
              </dl>
            ))}
          </div>
        </section>
      )}

      {hourlyResult && (
        <section aria-labelledby="hourly" className="grid gap-3">
          <h2 id="hourly" className="text-2xl font-semibold">
            从时薪算到年薪
          </h2>
          <p className="max-w-[70ch]">
            在“发薪周期”里选“小时”，再填每周工时。计算器把时薪乘以每周工时，再乘以 52
            周，然后按这个年薪算税。
          </p>
          <p className="max-w-[70ch]">
            例子：时薪 A${HOURLY_RATE}，每周工作 {DEFAULT_FORM.hoursPerWeek} 小时，税前年薪是{" "}
            {whole(hourlyResult.annual.gross)}。{fy} 财年税后每周到手约{" "}
            {formatAud(hourlyResult.perPeriod.week.takeHome)}。
          </p>
        </section>
      )}

      <section aria-labelledby="payg" className="grid gap-3">
        <h2 id="payg" className="text-2xl font-semibold">
          PAYG 预扣税和全年应缴的税
        </h2>
        <p className="max-w-[70ch]">
          雇主每期按 ATO 税率表从你的工资里扣税，这叫
          PAYG（按收入预扣税款）。计算器先算出你全年的税，再按发薪期数平摊，所以工资单上的数字可能相差几澳元。
        </p>
        <p className="max-w-[70ch]">
          雇主预扣的税和你全年应缴的税之间的差额，在你报税时结清。如果你勾选了标准扣除额，它也是在那时才扣减，所以雇主每期可能多预扣一点。
        </p>
        <SourceLine
          sources={[{ label: "ATO 税率表", href: TAX_TABLES_URL }]}
          financialYear={fy}
          checked={checked}
        />
      </section>

      <section aria-labelledby="rates" className="grid gap-3">
        <h2 id="rates" className="text-2xl font-semibold">
          {fy} 财年所得税税率：各州相同
        </h2>
        <p className="max-w-[70ch]">
          所得税由联邦征收，所以这些税率在 NSW、VIC、QLD、WA、SA、TAS、ACT 和 NT
          都适用。大多数人还要缴 {percent(year.medicare.rateBp)} 的 Medicare
          levy；收入低的人缴得少，或者不用缴。
        </p>
        <SimpleTable
          caption={`${fy} 财年居民所得税税率，不含 Medicare levy`}
          columns={["应税收入", `这部分收入的税款（${fy} 财年）`]}
          rows={bracketRows}
        />
        <SourceLine
          sources={[{ label: "ATO", href: year.resident.source.url }]}
          financialYear={fy}
          checked={checked}
        />
      </section>

      <section aria-labelledby="help" className="grid gap-3">
        <h2 id="help" className="text-2xl font-semibold">
          {fy} 财年 HELP 和学生贷款还款
        </h2>
        <p className="max-w-[70ch]">
          在“更多选项”里勾选学生贷款，就会加上强制还款。在最高一档，税率适用于你的全部收入，而不只是超出门槛的部分。
        </p>
        <SimpleTable
          caption={`${fy} 财年 HELP 和其他学生贷款还款税率`}
          columns={["还款收入", `还款（${fy} 财年）`]}
          rows={helpRows}
        />
        <p className="text-muted-foreground max-w-[70ch] text-sm">
          ATO 计算的收入不止应税收入，还包括例如应申报的附加福利和应申报的 super
          供款。计算器只用应税收入。
        </p>
        <SourceLine
          sources={[{ label: "ATO，学生贷款还款税率", href: year.studyLoan.source.url }]}
          financialYear={fy}
          checked={checked}
        />
      </section>
    </>
  ),
  faq: [
    {
      question: "在 NSW、VIC、QLD 或 WA，到手收入不一样吗？",
      answer:
        "不会。所得税、Medicare levy 和 HELP 还款都是联邦的，所以同样的薪水，在 NSW、VIC、QLD、WA、SA、TAS、ACT 和 NT 的到手收入相同。",
    },
    {
      question: "为什么我的第二份工作被扣的税更多？",
      answer:
        "免税额（tax-free threshold）只能在一个雇主那里使用。你的另一份工作从第一澳元起就要缴税，所以请在“更多选项”里填入主要工作的收入，看看第二份工作会增加多少。",
    },
    {
      question: "为什么一年有 26 个两周？",
      answer:
        "两周就是两个星期，一年有 52 周，所以有 26 个两周。即使某一年有 27 个发薪日，计算器也始终把全年金额除以 26。",
    },
    {
      question: "雇主的 super 算在到手收入里吗？",
      answer: `不算。雇主在薪水之外，把 ${percent(year.superGuarantee.rateBp)} 的 super guarantee（退休金强制供款）存入你的 super 账户，所以单独列出。如果你的薪资包含 super，请选“含 super”，计算器会先把它拆出来。`,
    },
    {
      question: `${standardDeduction} 标准扣除额是什么？`,
      answer: `这是一项法律规定（Treasury Laws Amendment (Tax Reform No. 1) Act 2026，Schedule 4）。从 ${fy} 财年起，领取已预扣税的工资、薪金及类似收入的澳大利亚居民，报税时可获得最高 ${standardDeduction} 的扣除额，由 ATO 自动适用。金额是 ${standardDeduction} 与这类收入中较小的一个，再减去你申报的、已被它涵盖的工作相关开支。它不适用于经营收入，所以个体经营者的经营利润不算在内。我们默认不计入，因为它取决于你自己的情况；要计入，请在“更多选项”里勾选。`,
    },
    {
      question: "Medicare levy 的低收入减免是怎么回事？",
      answer: `Medicare levy 是应税收入的 ${percent(year.medicare.rateBp)}。应税收入不超过 ${dollars(year.medicare.singleLower)} 的单身人士不用缴，不超过 ${dollars(year.medicare.singleUpper)} 的缴减少后的金额。${year.medicare.status === "provisional" ? `ATO 尚未公布 ${fy} 财年的门槛，所以计算器暂定使用 ${previousFy} 财年的数值。` : ""}家庭门槛这里不适用。有些人可以豁免 Medicare levy，例如有医疗豁免，或无权享受 Medicare 福利的人；外国居民也不用缴。如果你属于这些情况，请在“更多选项”里取消勾选“计入 Medicare levy”。Medicare levy surcharge（附加费）是另一回事，这里不计算。`,
    },
    {
      question: "外国居民和打工度假签证持有人怎么征税？",
      answer: `外国居民从第一澳元起缴税，税率不同，也不缴 Medicare levy。working holiday maker（打工度假签证持有人）有自己的税率。请在“更多选项”里更改“税务居民身份”。${year.foreignResident.status === "provisional" ? `ATO 尚未公布这两类人 ${fy} 财年的税率，所以计算器暂定使用 ${previousFy} 财年的税率，并标明为暂定。` : ""}`,
    },
    {
      question: "我用 ABN 工作，这个计算器适合我的收入吗？",
      answer: (
        <>
          不适合。它假设雇主每期替你预扣税并存入 super，而 ABN
          收入通常两样都没有，要自己留出税款。请用{" "}
          <a href="/sole-trader-tax-calculator" className={linkClass}>
            个体经营者税务计算器
          </a>{" "}
          看该留出多少。
        </>
      ),
    },
    {
      question: "这是税务建议吗？",
      answer: `不是。这是按 ATO ${fy} 财年税率和结果下方列出的假设做出的估算。你自己的情况，请向 ATO 或注册税务代理咨询。`,
    },
  ],
}
